import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form5227Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 5227 Instructions | Split-Interest Trust Return Guide "
          description="Complete IRS Form 5227 with step-by-step instructions for filing, income reporting, and compliance for split-interest trusts including CRATs, CRUTs, and CLTs."
          keywords=""
          canonical="resources/tax-exempt/form-5227-instructions/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          IRS{' '}
          <span className="highlight-container">
            <span className="highlight"> Form 5227 </span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          <Link
            href="/tax-exempt/e-file-irs-form-5227/"
            sx={{ color: '#0202ff' }}
          >
            {' '}
            E-file your IRS Form 5227
          </Link>{' '}
          for the <span style={{ fontWeight: 600 }}>2025 tax year</span> to
          report split-interest
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          trust financials and charitable distributions accurately.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5227instructions`}
          >
            E-file Form 5227 Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-5227-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 5227 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 5227? Form 5227, Split-Interest Trust
                          Information Return,
                        </strong>{' '}
                        is used to report the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          financial activities, charitable deductions, and
                          distributions
                        </strong>{' '}
                        of a split-interest trust and determine whether certain{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Chapter 42 excise taxes{' '}
                        </strong>
                        apply.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File Form 5227? Charitable remainder trusts,
                          pooled income funds, charitable lead trusts, and
                          certain other split-interest trusts
                        </strong>{' '}
                        generally must file Form 5227, subject to applicable IRS
                        exceptions.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Does Form 5227 Report?
                        </strong>{' '}
                        The return reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          income and deductions, capital gains and losses,
                          charitable distributions, trust assets, and other
                          information
                        </strong>{' '}
                        required for the applicable type of split-interest
                        trust.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Supporting Forms and Schedules:
                        </strong>{' '}
                        Depending on the trust's activities, Form 5227 may
                        require supporting forms and schedules such as{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-5227-instructions/#scha"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Schedule A (Form 5227)
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-5227-schedule-e/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Schedule E (Form 1040)
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-4797-sales-business-property/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 4797
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/schedule-d-form-1041-resource-guide/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Schedule D (Form 1041)
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 8949
                        </Link>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>Form 4720.</strong>
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Must File
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whenis" sx={{ color: '#0202ff' }}>
                          When is Form 5227 Due?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalty" sx={{ color: '#0202ff' }}>
                          Penalties for Noncompliance
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#trust" sx={{ color: '#0202ff' }}>
                          Trust Identification Information
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#income" sx={{ color: '#0202ff' }}>
                          Part I: Income and Deductions - Sections A to E
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sch" sx={{ color: '#0202ff' }}>
                          Part II: Schedule of Distributable Income (Section 664
                          Trusts Only)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part" sx={{ color: '#0202ff' }}>
                          Part III: Distributions of Principal for Charitable
                          Purposes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sheet" sx={{ color: '#0202ff' }}>
                          Part IV: Balance Sheet
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#chart" sx={{ color: '#0202ff' }}>
                          Part V: Charitable Remainder Annuity Trust (CRAT)
                          Information
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#remain" sx={{ color: '#0202ff' }}>
                          Part VI: Charitable Remainder Unitrust (CRUT)
                          Information
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#state" sx={{ color: '#0202ff' }}>
                          Part VII: Statements Regarding Activities
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#act" sx={{ color: '#0202ff' }}>
                          Part VIII: Statements on Activities Requiring Form
                          4720
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#question" sx={{ color: '#0202ff' }}>
                          Part IX: Questionnaire for Charitable Lead Trusts,
                          Pooled Income Funds, and Charitable Remainder Trusts
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule A (Not Open to Public Inspection){' '}
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Points and Best Practices
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who Must File
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 5227 must be filed by trustees of any trust organized as
                  one of the following:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }} component="ul">
                  <li style={{ marginBottom: 8 }}>
                    Charitable remainder annuity trusts (CRATs)
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Charitable remainder unitrusts (CRUTs)
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Charitable lead trusts (CLTs){' '}
                  </li>
                  <li>Pooled income funds </li>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These split-interest trusts are required to submit Form 5227
                  annually, providing a detailed report of their financial
                  activities, distributions, and charitable deductions.{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'column' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '16px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        Trustees of CRATs, CRUTs, CLTs, or pooled income
                        funds—Form 5227 filing is mandatory.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0 }}>
                        With TaxZerone, e-file quickly and ensure every detail
                        of trust income, distributions, and charitable
                        deductions is reported correctly.
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5227instructions`}
                      >
                        File Form 5227 Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Exception to Filing Requirement{' '}
                </Typography>
                <Typography variant="body1">
                  A split-interest trust is not required to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 5227
                  </Link>{' '}
                  if all these conditions apply:{' '}
                </Typography>
                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>The trust was created before May 27, 1969, and </li>
                  <li>
                    All corpus transfers to the trust occurred before May 27,
                    1969, or{' '}
                  </li>
                  <li>
                    For every transfer of corpus made after May 26, 1969, no
                    deduction was allowed under any of the sections listed in
                    IRC section 4947(a)(2).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, if a split-interest trust formed before May 27, 1969,
                  receives a contribution to corpus after that date for which a
                  deduction is allowed, it loses this exception status. In that
                  case, the trust must file Form 5227 starting with the tax year
                  when the contribution occurs and for every subsequent year
                  thereafter—just like any split-interest trust created on or
                  after May 27, 1969.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whenis"
                >
                  When is Form 5227 Due?
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      For Calendar-Year Trusts:
                    </span>{' '}
                    The filing deadline for Form 5227 reporting the 2025 tax
                    year is April 15, 2026.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      For Fiscal-Year Trusts:
                    </span>{' '}
                    Form 5227 must be filed by the 15th day of the fourth month
                    after the end of the trust’s fiscal year in 2026. For
                    example, if the fiscal year ends on June 30, 2026, the form
                    is due by October 15, 2026.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Extension of time to file:
                    </span>{' '}
                    Trustees may request a six-month extension to file Form 5227
                    by submitting{' '}
                    <Link
                      href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8868
                    </Link>{' '}
                    (Application for Extension of Time to File an Exempt
                    Organization Return) on or before the original filing
                    deadline (April 15, 2026, for calendar-year trusts). If
                    granted, the extended deadline will generally be October 15,
                    2026, for calendar-year trusts.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Important:</strong> Filing
                  late, even when no tax is due, may result in penalties. It is
                  critical to meet IRS deadlines to avoid unnecessary fees.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="penalty"
                >
                  Penalties for Noncompliance
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Failure to File Timely, Complete, or Correct Return:
                    </span>{' '}
                    The penalty is $25 per day (up to $12,500 per return). If
                    the trust has gross income over $318,500, the penalty is
                    $125 per day (up to $63,500 per return).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Failure to Comply with IRS Written Demand:
                    </span>{' '}
                    If the IRS sends a written demand, the penalty is $10 per
                    day up to $6,000 if not complied with by the specified date.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Trustee Penalty:</span> If
                    the trustee knowingly fails to file, the same penalty
                    applies to both the trust and the trustee.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Additional Penalties:
                    </span>{' '}
                    Penalties may apply for filing false or fraudulent returns
                    or if key information is missing. Incomplete or incorrect
                    returns can jeopardize compliance and charitable deductions.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="trust"
                >
                  Trust Identification Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In this section of{' '}
                  <span style={{ fontWeight: 600 }}>Form 5227</span>, trustees
                  provide the basic identifying details of the trust, including
                  its legal name, trustee information, address, type of entity,
                  financial values, and filing status. These details allow the
                  IRS to recognize the trust, confirm its structure, and track
                  compliance.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-trust-img.webp" />
                  <source srcSet="/assets/images/5227-trust-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-trust-img.png"
                    loading="lazy"
                    alt="Form 5227 showing trust information, including full name, trustee, address, EIN, entity type, fair market value, gross income, and return status."
                    width={752}
                    height={218}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Full Name of Trust</span>{' '}
                    – Enter the legal name of the trust as stated in the trust
                    agreement.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Name of Trustee</span> –
                    Provide the trustee’s full name. If multiple trustees exist,
                    list the primary contact.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Address</span> – Include
                    the number, street, and room or suite number. If using a
                    P.O. Box, follow IRS guidelines. Also provide the city,
                    state, ZIP code, and country (if foreign).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Employer Identification Number (EIN){' '}
                    </span>{' '}
                    – Report the trust’s EIN, not the trustee’s personal SSN.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Type of Entity </span> –
                    Indicate whether the trust is a:
                  </li>
                  <ol>
                    <li>Charitable lead trust </li>
                    <li>Charitable remainder annuity trust (CRAT) </li>
                    <li>Charitable remainder unitrust (CRUT) </li>
                    <li>Pooled income fund </li>
                    <li>Other (with explanation attached) </li>
                  </ol>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Fair Market Value of Assets{' '}
                    </span>{' '}
                    – State the total fair market value of all trust assets at
                    the end of the tax year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Gross Income </span> –
                    Report the trust’s total gross income before any deductions
                    or expenses.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Return Type/Status{' '}
                    </span>{' '}
                    – Check the appropriate box if this is an initial return,
                    final return, amended return (with amended K-1s if
                    necessary), or if trustee information has changed.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Date the Trust Was Created{' '}
                    </span>{' '}
                    – Enter the original date the trust was established
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="income"
                >
                  Part I: Income and Deductions - Sections A to E
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Section A: Ordinary Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-part1-income-img.webp" />
                  <source srcSet="/assets/images/5227-part1-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-part1-income-img.png"
                    loading="lazy"
                    alt="Form 5227 Part I, Section A showing the trust’s ordinary income, including details of revenue sources and taxable amounts."
                    width={750}
                    height={185}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={1}
                  sx={{ mb: 2, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>Interest Income</span>
                    <br />
                    List the total taxable and tax-exempt interest (excluding
                    municipal bond interest, which will be later in Section C).
                    This includes interest from bank accounts, corporate bonds,
                    notes receivable, etc.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  2a.{' '}
                  <span style={{ fontWeight: 600 }}>Ordinary Dividends</span>{' '}
                  <br />
                  Report the gross amount of dividends received from stocks,
                  mutual funds, and similar investments before any expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  2b.{' '}
                  <span style={{ fontWeight: 600 }}> Qualified Dividends</span>{' '}
                  <br />
                  Of the dividends received, identify and enter qualified
                  dividends (generally, those held long enough to qualify for
                  special tax treatment; check instructions for criteria).
                </Typography>
                <Box
                  component="ol"
                  start={3}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Business Income or (Loss)
                    </span>
                    <br />
                    Enter business income or loss for the trust. If the trust
                    operates or holds interests in a trade or business, attach
                    IRS Schedule C (Form 1040).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Rents, Royalties, Partnerships, Other Estates and Trusts
                    </span>
                    <br />
                    Enter net income or loss from rental real estate, royalties,
                    and income from partnerships or other entities. Attach IRS
                    Schedule E (Form 1040).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Farm Income or (Loss)
                    </span>
                    <br />
                    List net farm income or loss. Attach IRS Schedule F (Form
                    1040) if the trust is engaged in farming activities.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Ordinary Gain or (Loss)
                    </span>
                    <br />
                    Report gains or losses on the sale of property used in a
                    trade or business (not the typical capital assets). Attach
                    Form 4797.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Other Income</span>
                    <br />
                    Specify any other ordinary income sources not previously
                    reported. Provide both the type and the dollar amounts in an
                    attached statement if there are multiple other income types.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Total Ordinary Income
                    </span>
                    <br />
                    Add together lines 1, 2a, and 3 through 7, and enter the
                    total on line 8.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Section B: Capital Gains (Losses)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-capital-gain-img.webp" />
                  <source srcSet="/assets/images/5227-capital-gain-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-capital-gain-img.png"
                    loading="lazy"
                    alt="Form 5227 Part I, Section B showing the trust’s capital gains and losses, including details of investments sold during the tax year."
                    width={751}
                    height={102}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={9}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Short-Term Capital Gain or (Loss)
                    </span>
                    <br />
                    Enter all net short-term capital gains and losses. Attach
                    IRS Schedule D, Part I (Form 1041), detailing capital
                    transactions of assets held one year or less.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Long-Term Capital Gain or (Loss)
                    </span>
                    <br />
                    Report the net total of long-term capital gains and losses
                    (assets held more than one year). Use Schedule D, Part II
                    (Form 1041).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Unrecaptured Section 1250 Gain
                    </span>
                    <br />
                    If the trust sold depreciated real estate, report any
                    recapture income relating to this special category.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>28% Rate Gain</span>
                    <br />
                    Report gains that are subject to the 28% maximum tax rate
                    (e.g., gains on collectibles and certain small business
                    stock).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Total Capital Gains (Losses)
                    </span>
                    <br />
                    Combine amounts from lines 9 and 10 for a net capital gain
                    or loss.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Section C: Nontaxable Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-nontaxable-img.webp" />
                  <source srcSet="/assets/images/5227-nontaxable-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-nontaxable-img.png"
                    loading="lazy"
                    alt="Form 5227 Part I, Section C showing the trust’s nontaxable income, including amounts excluded from taxable income for the year."
                    width={752}
                    height={85}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={14}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>Tax-Exempt Interest</span>
                    <br />
                    Report interest on municipal or other tax-exempt bonds.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Other Nontaxable Income
                    </span>
                    <br />
                    Specify other types of nontaxable income (e.g., life
                    insurance proceeds, gifts). Provide both type and amount.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Total Nontaxable Income
                    </span>
                    <br />
                    Add lines 14 and 15.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Section D: Deductions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-deduction-img.webp" />
                  <source srcSet="/assets/images/5227-deduction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-deduction-img.png"
                    loading="lazy"
                    alt="Form 5227 Part I, Section D showing the trust’s deductions, including allowable expenses and amounts subtracted from total income. "
                    width={750}
                    height={135}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={17}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>Interest</span>
                    <br />
                    Total interest expense paid by the trust (e.g., on loans,
                    mortgages).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Taxes</span>
                    <br />
                    Taxes paid by the trust that are deductible (property taxes,
                    state income taxes, etc.). Federal income taxes cannot be
                    deducted.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Trustee Fees</span>
                    <br />
                    Enter total fees paid to trustees.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Attorney, Accountant, and Return Preparer Fees
                    </span>
                    <br />
                    Sum all professional fees paid in connection with trust
                    administration.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Other Allowable Deductions
                    </span>
                    <br />
                    List other deductions permitted under the tax code
                    (insurance, safe deposit box rental, etc.). Attach a
                    detailed itemization if necessary.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Total Deductions</span>
                    <br />
                    Add the amounts from lines 17 to 21.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Charitable Deduction
                    </span>
                    <br />
                    Report the total amount distributed or set aside for
                    charitable purposes that’s eligible for a charitable
                    deduction.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Section E: Deductions Allocable to Income Categories (Section
                  664 Trusts Only)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-allocable-img.webp" />
                  <source srcSet="/assets/images/5227-allocable-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-allocable-img.png"
                    loading="lazy"
                    alt="Form 5227 Part I, Section E showing deductions allocated to different income categories, detailing how expenses reduce specific income types."
                    width={753}
                    height={118}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  24a.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Allocable Ordinary Income Deductions
                  </span>
                  <br />
                  Enter the portion of total deductions from line 22 that is
                  attributable to ordinary income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  b.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Ordinary Income Less Allocable Deductions
                  </span>
                  <br />
                  Subtract 24a from 8, and enter the result.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  25a.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Allocable Capital Gain Deductions
                  </span>
                  <br />
                  List the portion of deductions that pertain to capital gain.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  b.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Capital Gain Less Allocable Deductions
                  </span>
                  <br />
                  Subtract 25a from 13, and enter the result.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  26a.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Allocable Nontaxable Income Deductions
                  </span>
                  <br />
                  Report the part of deductions related to nontaxable income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  b.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Nontaxable Income Less Allocable Deductions
                  </span>
                  <br />
                  Subtract 26a from 16, and enter the remaining nontaxable
                  income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="sch"
                >
                  Part II: Schedule of Distributable Income (Section 664 Trusts
                  Only)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section tracks the amounts of income the trust has
                  available for distribution, categorized by ordinary income,
                  capital gains, and nontaxable income. It also accounts for the
                  special Net Investment Income (NII) classifications relevant
                  post-2012.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-part-2-img.webp" />
                  <source srcSet="/assets/images/5227-part-2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-part-2-img.png"
                    loading="lazy"
                    alt="Form 5227 Part II showing the schedule of distributable income, detailing amounts available for distribution to trust beneficiaries."
                    width={753}
                    height={168}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Lines 1-3: </span>
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 1:</span> Enter any
                      undistributed income carried forward from prior tax years,
                      divided into ordinary income (a), capital gains/losses
                      (b), and nontaxable income (c).{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 2:</span> Report
                      current tax year net income before distributions, sourcing
                      amounts from Part I (lines 24b, 25b, and 26b) for each
                      category.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 3:</span> Sum
                      undistributed income (line 1) and current year income
                      (line 2) for total distributable income.
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      NII Classification Columns:{' '}
                    </span>
                  </li>
                  <ul>
                    <li>
                      Distinguish between &quot;Excluded Income&quot; and
                      &quot;Accumulated NII post 2012&quot; within each income
                      category for tax and reporting accuracy.
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="part"
                >
                  Part III: Distributions of Principal for Charitable Purposes
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  This section accounts for the use of trust principal to
                  fulfill charitable purposes.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-principal-img.webp" />
                  <source srcSet="/assets/images/5227-principal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-principal-img.png"
                    loading="lazy"
                    alt="Form 5227 Part III Section A showing charitable distributions of principal."
                    width={749}
                    height={317}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Section A (Lines 1-4):{' '}
                    </span>
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 1:</span> Enter
                      total principal amounts distributed for charitable
                      purposes in prior tax years.{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 2:</span> Report
                      principal distributed in the current tax year for
                      charitable purposes.
                      <br />
                      For each distribution, provide:
                    </li>
                    <ul>
                      <li>(A) Payee&apos;s full name and address. </li>
                      <li>(B) Date of distribution. </li>
                      <li>
                        (C) Charitable purpose and a description of any non-cash
                        assets distributed.{' '}
                      </li>
                    </ul>

                    <li>
                      <span style={{ fontWeight: 600 }}>Line 3:</span> Total all
                      amounts reported on attachments for distributions beyond
                      the main form space.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 4:</span> Add lines
                      1 through 3 for total principal charitable distributions.
                    </li>
                  </ul>
                </Box>
                <picture>
                  <source srcSet="/assets/images/5227-accumulated-img.webp" />
                  <source srcSet="/assets/images/5227-accumulated-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-accumulated-img.png"
                    loading="lazy"
                    alt="Form 5227 Part III, Section B showing income set aside and income distributions for charitable purposes."
                    width={760}
                    height={490}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Section B</span>(Lines
                    5-10): Accumulated Income Set Aside and Income Distributions
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>Lines 5a & 5b:</span>{' '}
                      Report income set aside in prior years for which a
                      charitable deduction was taken (per IRC Section 642(c))
                      and the current year&apos;s charitable deduction from Part
                      I, line 23.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 6:</span> Sum of
                      lines 5a and 5b.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Lines 7a-7c:</span>{' '}
                      Report distributions made during the year against amounts
                      set aside in prior and current years, with full payee,
                      date, and purpose details.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 8:</span> Total
                      current year distributions attached.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 9:</span> Sum of
                      distributions (lines 7 + 8).
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 10:</span>{' '}
                      Carryover amount, calculated by subtracting line 9 from
                      line 6, indicating income set aside but not yet
                      distributed.
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>Grantor Trusts:</span>{' '}
                    Only complete lines 7-9 in Section B.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="sheet"
                >
                  Part IV: Balance Sheet
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Report the trust&apos;s financial position as of the start and
                  end of the tax year.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-bal-assets-img.webp" />
                  <source srcSet="/assets/images/5227-bal-assets-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-bal-assets-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IV balance sheet showing trust assets, including cash, investments, receivables, and total assets at year end."
                    width={760}
                    height={408}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Lines 1-13: Assets </span>
                  </li>
                  <ul style={{ marginTop: -10 }}>
                    <li>
                      Include all categories such as cash (interest-bearing and
                      non-interest), receivables (with allowance for doubtful
                      accounts), notes/loans, inventories, prepaid expenses,
                      investments (government, corporate stocks/bonds, real
                      estate, equipment), and other assets with
                      descriptions.{' '}
                    </li>
                    <li>
                      Provide beginning and ending book values and the fair
                      market value (FMV) for each asset category.
                    </li>
                  </ul>
                </Box>
                <picture>
                  <source srcSet="/assets/images/5227-liabilities-img.webp" />
                  <source srcSet="/assets/images/5227-liabilities-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-liabilities-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IV, lines 14–19 showing trust liabilities."
                    width={751}
                    height={119}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Lines 14-19: Liabilities{' '}
                    </span>
                  </li>
                  <ul>
                    <li>
                      Accounts payable, accrued expenses, deferred revenue,
                      loans from disqualified persons, mortgages, and other
                      liabilities.{' '}
                    </li>
                    <li>
                      Attach supporting statements for complex liabilities.
                    </li>
                  </ul>
                </Box>
                <picture>
                  <source srcSet="/assets/images/5227-net-assets-img.webp" />
                  <source srcSet="/assets/images/5227-net-assets-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-net-assets-img.png"
                    loading="lazy"
                    alt="Lines 20-23: Net Assets"
                    width={747}
                    height={118}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Lines 20-23: Net Assets
                    </span>
                  </li>
                  <ul>
                    <li>
                      Principal or corpus, undistributed income (ordinary,
                      capital gains, and nontaxable).{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 22:</span> Sum of
                      total net assets.{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Line 23:</span> Total
                      liabilities plus net assets (should balance with total
                      assets).
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="chart"
                >
                  Part V: Charitable Remainder Annuity Trust (CRAT) Information
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  For CRATs governed under Section 664(d)(1) only.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-part5-img.webp" />
                  <source srcSet="/assets/images/5227-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-part5-img.png"
                    loading="lazy"
                    alt=" Form 5227 Part V: Charitable Remainder Annuity Trust (CRAT) information. "
                    width={735}
                    height={51}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1a: </span> Enter the
                    initial fair market value of property placed into the trust.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1b: </span> Enter the
                    total annual annuity amounts paid to all recipients.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="remain"
                >
                  Part VI: Charitable Remainder Unitrust (CRUT) Information
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  For CRUTs governed under Section 664(d)(2).
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-remainder-img.webp" />
                  <source srcSet="/assets/images/5227-remainder-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-remainder-img.png"
                    loading="lazy"
                    alt=" Form 5227 Part VI: Charitable Remainder Unitrust (CRUT) information"
                    width={717}
                    height={497}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1: </span> Indicate
                    if the CRUT is a Net Income Charitable Remainder Unitrust
                    (NICRUT).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 2: </span> Indicate
                    if it is a Net Income with Make-up CRUT (NIMCRUT).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 3: </span> State
                    whether the trust changed its payment method during the
                    year; if yes, provide a detailed explanation.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 4a: </span> Enter the
                    fixed percentage unitrust payout.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 4b: </span> Calculate
                    and enter the unitrust amount by subtracting liabilities
                    from assets FMV, multiplied by the percentage.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 5-8: </span> These
                    lines address trust accounting income, accumulated
                    distributions, and final required distributions for the
                    year, as well as any carryover of distribution deficiencies
                    for NIMCRUTs.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="state"
                >
                  Part VII: Statements Regarding Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-regard-img.webp" />
                  <source srcSet="/assets/images/5227-regard-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-regard-img.png"
                    loading="lazy"
                    alt="Form 5227 Part VII: Statements Regarding Activities and compliance information."
                    width={744}
                    height={117}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1: </span> Confirm
                    whether IRC section 508(e) requirements are met (governing
                    instrument amendments or state law compliance).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 2: </span> Indicate
                    if the return only reports income/assets of segregated
                    amounts under Section 4947(a)(2)(B).
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="act"
                >
                  Part VIII: Statements on Activities Requiring Form 4720
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-part8-img.webp" />
                  <source srcSet="/assets/images/5227-part8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-part8-img.png"
                    loading="lazy"
                    alt="Form 5227 Part VIII: Statements Regarding Activities that may require filing Form 4720"
                    width={674}
                    height={836}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  If any of these activities apply,{' '}
                  <Link
                    href="https://www.irs.gov/forms-pubs/about-form-4720"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 4720
                  </Link>{' '}
                  must be filed unless an exception applies.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Self-Dealing (Section 4941):{' '}
                    </span>{' '}
                    Activities with disqualified persons related to sales,
                    loans, reimbursements, compensation, or transfers.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Excess Business Holdings (Section 4943):{' '}
                    </span>{' '}
                    Trust ownership in businesses beyond allowed thresholds.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Jeopardizing Investments (Section 4944):{' '}
                    </span>{' '}
                    Investments threatening the charitable purpose.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Taxable and Political Expenditures (Sections 4945 &
                      4955):{' '}
                    </span>{' '}
                    Involvement in lobbying, political campaigns, grants outside
                    charitable purposes, or disallowed expenditures.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Personal Benefit Contracts (Section 170(f)(10)):{' '}
                    </span>{' '}
                    Contracts involving personal benefit premiums.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Trustees must answer these questions truthfully, provide
                  explanations, and file Form 4720 if required.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="question"
                >
                  Part IX: Questionnaire for Charitable Lead Trusts, Pooled
                  Income Funds, and Charitable Remainder Trusts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-indicate-img.webp" />
                  <source srcSet="/assets/images/5227-indicate-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-indicate-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IX Section A: Questionnaire for all Charitable Lead Trusts, Pooled Income Funds, and Charitable Remainder Trusts"
                    width={751}
                    height={119}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Section A: </span>{' '}
                    Indicate if any income interests expired during the year or
                    prior years.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/5227-lead-trusts-img.webp" />
                  <source srcSet="/assets/images/5227-lead-trusts-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-lead-trusts-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IX Section B: Questionnaire for Charitable Lead Trusts"
                    width={748}
                    height={118}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Section B (Charitable Lead Trusts):{' '}
                    </span>{' '}
                    Report excess income requirements and required payments to
                    charitable beneficiaries.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/5227-pooled-img.webp" />
                  <source srcSet="/assets/images/5227-pooled-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-pooled-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IX Section C: Questionnaire for Pooled Income Funds"
                    width={754}
                    height={169}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Section C (Pooled Income Funds):{' '}
                    </span>{' '}
                    Provide details of contributions received, required
                    distributions to remainder beneficiaries, and any
                    undistributed remainder amounts.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/5227-secd-img.webp" />
                  <source srcSet="/assets/images/5227-secd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-secd-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IX Section D: Questionnaire for Charitable Remainder Trusts "
                    width={740}
                    height={396}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Section D (Charitable Remainder Trusts):{' '}
                    </span>{' '}
                    Declare if the trust involves cemeteries or veterans’ posts,
                    elections related to income timing, trust instrument
                    amendments, final distributions, foreign account interests
                    (FinCEN Form 114 requirements), etc.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="scha"
                >
                  Schedule A (Not Open to Public Inspection)
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    mb: 1,
                    mt: 1,
                    fontWeight: 600,
                  }}
                >
                  Overview of Schedule A{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Schedule A of Form 5227 is a confidential attachment used by
                  Section 664 charitable remainder trusts (CRTs) and charitable
                  lead trusts to report detailed accumulations of income,
                  distributions, asset donations, and donor information. Unlike
                  the main Form 5227, Schedule A is not open to public
                  inspection to protect sensitive donor and distribution data.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    mb: 1,

                    fontWeight: 600,
                  }}
                >
                  Part I: Accumulation Schedule (Section 664 Trusts Only)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  This schedule tracks trust income accumulations by type and
                  their classification under Net Investment Income (NII)
                  rules.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-scha-income-img.webp" />
                  <source srcSet="/assets/images/5227-scha-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-scha-income-img.png"
                    loading="lazy"
                    alt="Form 5227 Part IX Section D: Questionnaire for Charitable Remainder Trusts"
                    width={753}
                    height={219}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1: </span> Total
                    Distributable Income <br />
                    Enter the total distributable income amount from Part II,
                    line 3 of the main Form 5227. Break it down into:
                  </li>
                  <ul>
                    <li>(a) Ordinary income </li>
                    <li>(b) Capital gain (loss) </li>
                    <li>
                      (c) Nontaxable income <br />
                      Additionally, classify each as &quot;Excluded Income&quot;
                      or &quot;Accumulated NII post 2012&quot; within their
                      respective columns, following applicable IRS NII rules.
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 2a: </span> Total
                    Distributions for 2025 <br />
                    Enter total amounts distributed during the tax year across
                    all income categories.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 2b: </span> 2025
                    Distributions from Income
                    <br />
                    Specify the portion of those 2025 distributions attributable
                    solely to income (versus principal/corpus).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 3: </span>{' '}
                    Undistributed Income at End of Tax Year
                    <br />
                    Calculate the remaining undistributed income by subtracting
                    line 2b from line 1 for each income type and classification.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    mb: 1,

                    fontWeight: 600,
                  }}
                >
                  Part II: Simplified Net Investment Income (NII) Calculation
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Track the NII components consistently during the year:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-net-income-img.webp" />
                  <source srcSet="/assets/images/5227-net-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-net-income-img.png"
                    loading="lazy"
                    alt="Schedule A Part II Line1: Simplified Net Investment Income Calculation "
                    width={751}
                    height={52}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Column (a) Accumulated NII:{' '}
                    </span>{' '}
                    Enter prior years’ accumulated NII amounts.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Column (b) Current Year NII:{' '}
                    </span>{' '}
                    Enter current year NII amounts.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Column (c) Distributions:{' '}
                    </span>{' '}
                    Report distributions reducing NII.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Column (d) Ending NII:{' '}
                    </span>{' '}
                    Calculate ending NII balance after distributions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This simplified schedule helps ensure proper tracking of NII
                  for taxation and reporting purposes.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    mb: 1,

                    fontWeight: 600,
                  }}
                >
                  Part III: Current Distributions Schedule (Section 664 Trusts
                  Only)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Report current year distributions with detailed recipient
                  information.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-current-sch-img.webp" />
                  <source srcSet="/assets/images/5227-current-sch-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-current-sch-img.png"
                    loading="lazy"
                    alt="Schedule A Part III Line 1a-4: Current Distributions Schedule"
                    width={745}
                    height={250}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Columns to fill:
                    </span>{' '}
                  </li>
                  <ul style={{ listStyleType: 'none' }}>
                    <li>(a) Recipient’s full name and address </li>
                    <li>
                      (b) Recipient’s identifying number (e.g., social security
                      or EIN) — only here as Schedule A is confidential{' '}
                    </li>
                    <li>
                      (c) Percentage of total unitrust payable if
                      applicable{' '}
                    </li>
                    <li>(d) Ordinary income distributed </li>
                    <li>(e) Short-term capital gains distributed </li>
                    <li>(f) Long-term capital gains distributed </li>
                    <li>(g) Nontaxable income distributed </li>
                    <li>(h) Corpus or principal distributed </li>
                    <li>
                      (i) Total distribution amount (sum of columns d through
                      h){' '}
                    </li>
                    <li>(j) Net investment income distributed </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 2: </span> For more
                    recipients than fit on the form, attach a schedule
                    continuing the same details, then summarize attachment
                    totals on line 2.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 3: </span> Total
                    distributions to all recipients; the sum of amounts shown in
                    column (i) including attachments.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 4: </span> If the
                    total on line 3 column (i) does not match the corresponding
                    amounts reported elsewhere on the main Form 5227 (Part V
                    line 1b for CRATs or Part VI line 7 for CRUTs), check this
                    box and attach a detailed explanation.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 17, sm: 17, xs: 16 },
                    mb: 1,

                    fontWeight: 600,
                  }}
                >
                  Part IV: Current Distributions for Charitable Lead Trusts or
                  Pooled Income Funds Only
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-part4-total-income-img.webp" />
                  <source srcSet="/assets/images/5227-part4-total-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-part4-total-income-img.png"
                    loading="lazy"
                    alt="Schedule A Part IV Line 1: Current Distributions"
                    width={734}
                    height={34}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1: </span> Enter the
                    total amount required to be paid to private beneficiaries
                    (non-charitable recipients) in the current tax year.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 17, sm: 17, xs: 16 },
                    mb: 1,

                    fontWeight: 600,
                  }}
                >
                  Part V: Assets and Donor Information (Section 664 Trusts or
                  Charitable Lead Trusts Only)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/5227-assets-img.webp" />
                  <source srcSet="/assets/images/5227-assets-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/5227-assets-img.png"
                    loading="lazy"
                    alt="Schedule A Part V Line 1-5 : Assets and Donor Information"
                    width={756}
                    height={354}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 1: </span> Initial
                    Return or Additional Contributions for 2025
                    <br />
                    If this is the trust’s first return or if new assets were
                    contributed during 2025, answer &quot;Yes.&quot; Otherwise,
                    answer &quot;No.&quot;
                  </li>
                  <li>
                    Schedule below Line 1 if “Yes”: <br />
                    Provide:
                  </li>
                  <ul style={{ listStyleType: 'none' }}>
                    <li>(a) Donor name and address </li>
                    <li>(b) Description of each donated asset </li>
                    <li>
                      (c) Fair Market Value (FMV) of each asset on the donation
                      date{' '}
                    </li>
                    <li>(d) Date of donation </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}> Lines 2a to 4:</span>
                    <br />
                    Complete as applicable, summarizing donations made, with
                    total FMV reported on Line 4.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Line 5: </span> Final
                    Year Termination Agreement
                    <br />
                    For charitable remainder trusts, if this was the final
                    filing year, indicate whether an early termination agreement
                    was signed by all parties. If yes, attach a copy of the
                    signed agreement.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="key"
                >
                  Key Points and Best Practices
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Schedule A is confidential, ensure this form is handled
                    securely and separately from public filings.
                  </li>
                  <li>
                    Attach supplemental schedules for additional recipients or
                    donations if space is insufficient.{' '}
                  </li>
                  <li>
                    Reconciliation between Schedule A distributions and the main
                    Form 5227 is crucial to avoid IRS inquiries.{' '}
                  </li>
                  <li>
                    Keep thorough documentation supporting FMV calculations,
                    donor identity, asset descriptions, and distribution
                    purposes.{' '}
                  </li>
                  <li>
                    Filing Schedule A accurately reflects compliance with
                    section 664 trust rules and ensures trust tax reporting
                    integrity.{' '}
                  </li>
                </Box>

                {/* <Alert
                  severity="warning"
                
                  icon={false}
                  sx={{ mb: 4 }}
                >
                  <strong style={{fontWeight:600}} >Note:</strong> If the deadline falls on the federal holiday or
                  weekend, it is extended to the next business day.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  FAQs
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  1. What information must be reported on Form 5227?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  Details include trust identification, income and deduction
                  breakdowns, distributable income schedules, distributions to
                  beneficiaries, asset and liability balances, and specific
                  information for CRATs and CRUTs.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  2. Is Schedule A part of Form 5227 public?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  No, Schedule A contains sensitive distributions and donor
                  information and is exempt from public inspection to protect
                  privacy.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  3. What are the penalties for not filing or filing late?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  Penalties for not filing or filing late can include a fine of
                  $25 per day, up to $12,500 per return, or $125 per day, up to
                  $63,500 for larger trusts. Failure to file or filing
                  inaccurately may also jeopardize the trust’s tax-exempt
                  status.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  4. Do I need to file Form 1041 in addition to Form 5227?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  Typically, no. Split-interest trusts use Form 5227 instead of
                  Form 1041 for reporting. However, certain situations may
                  require both forms; consult a tax advisor.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  5. Can I request an extension to file Form 5227?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  Yes, trustees can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 8868
                  </Link>{' '}
                  to request an automatic 6-month extension before the original
                  due date. This extends the filing deadline but not the
                  deadline to pay any tax owed.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  6. How long is the extension for Form 5227?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 4 }}>
                  You can request a 6-month extension to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 5227
                  </Link>{' '}
                  by submitting{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 8868
                  </Link>{' '}
                  before the original deadline. For calendar-year trusts, this
                  typically extends the filing deadline from April 15 to October
                  15 of the filing year. Note that the extension only applies to
                  filing, not to payment.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        <ul>
                          <li style={{ marginBottom: 8 }}>
                            Phone assistance: 877-829-5500 (Mon-Fri){' '}
                          </li>
                          <li>
                            Official instructions and 5227 forms online at{' '}
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-5227"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              IRS.gov/Form5227
                            </Link>{' '}
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to E-file your Form 5227?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure your split-interest trust stays compliant with the IRS.
              <br />
              TaxZerone makes it simple, accurate, and secure to e-file your
              Form 5227 online.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5227instruction`}
              >
                E-file Form 5227 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form5227Instructions;
